import React, { useState, useEffect } from "react";
import { Search, ChevronDown, MoreVertical, Eye, MessageSquare, UserPlus, Edit3 } from "lucide-react";
import { useSupportTickets } from "../../../../hooks/useSupportTickets";
import TicketDetailModal from "./TicketDetailModal";
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  PRIORITY_COLORS,
  STATUS_COLORS
} from "../../../../constant/constant";
import SupportMetrics from "../../../../components/AdminDashboard/Support/components/SupportMetrics";

const SupportTicketsTable = () => {
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    category: ''
  });
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    tickets,
    loading,
    error,
    pagination,
    fetchTickets,
    updateTicketStatus,
    assignTicket
  } = useSupportTickets();

  // Fetch tickets when component mounts or filters change
  useEffect(() => {
    fetchTickets({
      page: currentPage,
      limit: 10,
      ...filters
    });
  }, [fetchTickets, currentPage, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleViewTicket = (ticketId) => {
    setSelectedTicketId(ticketId);
    setShowTicketModal(true);
  };

  const handleCloseModal = () => {
    setShowTicketModal(false);
    setSelectedTicketId(null);
  };

  const handleTicketUpdate = async (ticketId, updateData) => {
    try {
      if (updateData.status) {
        await updateTicketStatus(ticketId, updateData.status);
      }
      // Refresh tickets
      await fetchTickets({
        page: currentPage,
        limit: 10,
        ...filters
      });
    } catch (error) {
      console.error('Failed to update ticket:', error);
    }
  };

  const handleTicketResponse = async (ticketId, message, isInternal) => {
    try {
      // This will be handled by the modal, but we can refresh the tickets here
      await fetchTickets({
        page: currentPage,
        limit: 10,
        ...filters
      });
    } catch (error) {
      console.error('Failed to respond to ticket:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };


  // Use mock data for now, replace with real tickets when API is ready
  const displayTickets = tickets.length > 0 ? tickets : [];

  return (
    <>
      <SupportMetrics />

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Support Tickets</h2>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Search Bar */}
            <div className="relative flex-1 sm:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search tickets"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Filter Dropdowns */}
            <div className="flex gap-2">
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              >
                <option value="">All Status</option>
                {Object.values(TICKET_STATUSES).map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              >
                <option value="">All Priority</option>
                {Object.values(TICKET_PRIORITIES).map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              >
                <option value="">All Categories</option>
                {Object.values(TICKET_CATEGORIES).map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
            <span className="ml-2 text-gray-600">Loading tickets...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Table */}
        {!loading && displayTickets.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Ticket</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">User</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Category</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Priority</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Created</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Last Update</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {displayTickets.map((ticket) => (
                  <tr key={ticket.ticketId} className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-4">
                      <div>
                        <div className="font-medium text-gray-900 text-sm sm:text-base">{ticket.subject}</div>
                        <div className="text-xs text-@gray-500 mt-1">{ticket.ticketId}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{ticket.userName}</div>
                        <div className="text-xs text-gray-500">{ticket.userEmail}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        {ticket.category}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[ticket.priority] || 'bg-gray-100 text-gray-800'}`}>
                        {ticket.priority}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[ticket.status] || 'bg-gray-100 text-gray-800'}`}>
                        {ticket.status}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600">{formatDate(ticket.createdAt)}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">{formatDate(ticket.lastActivity)}</td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewTicket(ticket.ticketId)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleViewTicket(ticket.ticketId)}
                          className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                          title="Respond"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                        {/* {!ticket.assignedTo && (
                        <button
                          className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                          title="Assign"
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                      )} */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {!loading && displayTickets.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <MessageSquare className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        )}

        {/* Pagination */}
        {!loading && displayTickets.length > 0 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * (pagination.limit || 10)) + 1} to {Math.min(currentPage * (pagination.limit || 10), pagination.total || 0)} of {pagination.total || 0} results
            </div>
            {pagination.totalPages > 1 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-2 text-sm text-gray-700">
                  Page {currentPage} of {pagination.totalPages || 1}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages || 1))}
                  disabled={currentPage === (pagination.totalPages || 1)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Ticket Detail Modal */}
        <TicketDetailModal
          isOpen={showTicketModal}
          onClose={handleCloseModal}
          ticketId={selectedTicketId}
          onUpdateTicket={handleTicketUpdate}
          onRespondToTicket={handleTicketResponse}
        />
      </div>
    </>
  );
};

export default SupportTicketsTable;
